import { Check, RefreshCw, Search, Send, UserPlus } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getCurrentUserId } from 'common-src/features/auth';

import useCustomSelector from 'src/hooks/useCustomSelector';

import {
  assignLeadTaskToMe,
  createPatientFromLead,
  fetchLeadTaskQueue,
  markLeadTaskComplete,
  sendLeadConsentForm,
} from '../../services/careOrchestrationApi';
import LeadResourceCallForm from '../LeadResourceCallForm';
import { formatLeadName, groupTasksByLead, isOpenStatus } from './leadGrouping';
import styles from './LeadTasksView.module.scss';

const STATUS_LABELS = {
  0: 'Scheduled',
  1: 'New',
  2: 'In progress',
  3: 'Completed',
  4: 'Canceled',
  5: 'Unable to complete',
};

const formatDueDate = (value) => {
  if (!value) return 'No due date';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'No due date';
  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const getDecisionOptions = (task) => {
  const answers = task?.metadata?.dependency?.answers;
  return Array.isArray(answers) ? answers.filter((answer) => answer?.value && answer?.label) : [];
};

const LeadTasksView = ({ onCountChange, requestedTaskId, onSelectedTaskChange }) => {
  const currentUserId = useCustomSelector(getCurrentUserId);
  const [tasks, setTasks] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState(requestedTaskId || null);
  // A deep-linked task may already be completed, so start unfiltered when one is requested.
  const [status, setStatus] = useState(requestedTaskId ? 'all' : 'open');
  const [owner, setOwner] = useState('all');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [activeAction, setActiveAction] = useState(null);
  const [error, setError] = useState('');

  // Read through a ref so a URL change does not re-trigger the fetch.
  const requestedTaskIdRef = useRef(requestedTaskId);
  useEffect(() => {
    requestedTaskIdRef.current = requestedTaskId;
  }, [requestedTaskId]);

  const loadTasks = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await fetchLeadTaskQueue({ status, owner, search: appliedSearch });
      const nextTasks = response?.data || [];
      setTasks(nextTasks);
      onCountChange?.(response?.total ?? nextTasks.length);
      setSelectedTaskId((currentId) => {
        if (nextTasks.some((task) => task.id === currentId)) return currentId;
        const requested = requestedTaskIdRef.current;
        if (requested && nextTasks.some((task) => task.id === requested)) return requested;
        return nextTasks[0]?.id || null;
      });
    } catch (requestError) {
      setError(requestError.message || 'Unable to load lead tasks');
    } finally {
      setIsLoading(false);
    }
  }, [appliedSearch, onCountChange, owner, status]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  // Follow the URL when it changes underneath us (paste, back/forward, notification link).
  useEffect(() => {
    if (requestedTaskId && requestedTaskId !== selectedTaskId) {
      setSelectedTaskId(requestedTaskId);
    }
  }, [requestedTaskId, selectedTaskId]);

  // Keep the URL pointing at whatever is on screen so it can be copied and shared.
  // Notify once per id: the callback identity changes on every navigation, and without
  // this guard a route rewrite elsewhere would bounce against this effect forever.
  const lastNotifiedTaskIdRef = useRef(null);
  useEffect(() => {
    if (!selectedTaskId || lastNotifiedTaskIdRef.current === selectedTaskId) return;
    lastNotifiedTaskIdRef.current = selectedTaskId;
    onSelectedTaskChange?.(selectedTaskId);
  }, [onSelectedTaskChange, selectedTaskId]);

  const leadGroups = useMemo(() => groupTasksByLead(tasks), [tasks]);

  const selectedTask = useMemo(
    () => tasks.find((task) => task.id === selectedTaskId) || null,
    [selectedTaskId, tasks],
  );

  const selectedLead = useMemo(
    () => leadGroups.find((group) => group.leadResourceId === selectedTask?.leadResourceId) || null,
    [leadGroups, selectedTask],
  );

  // Selecting a lead lands on the task the workflow is waiting on.
  const selectLead = (group) => setSelectedTaskId(group.activeTask?.id || group.tasks[0]?.id);
  const isClaimedByCurrentUser = selectedTask?.assignee === currentUserId;
  const decisionOptions = getDecisionOptions(selectedTask);

  const runAction = async (actionName, action) => {
    if (!selectedTask || activeAction) return;
    setActiveAction(actionName);
    setError('');
    try {
      await action();
      await loadTasks();
    } catch (requestError) {
      setError(requestError.message || 'Unable to update lead task');
    } finally {
      setActiveAction(null);
    }
  };

  const isCreatePatientTask = selectedTask?.taskKey === 'create_patient_from_lead_manual';

  const createPatient = () =>
    runAction('createPatient', () => createPatientFromLead(selectedTask.leadResourceId));

  const isConsentTask = selectedTask?.taskKey === 'send_consent_form';
  const consentDispatch = selectedTask?.metadata?.consent_dispatch || null;

  const sendConsent = () =>
    runAction('consent', () =>
      sendLeadConsentForm(selectedTask.leadResourceId, { leadTaskId: selectedTask.id }),
    );

  const completeTask = (decision = null) =>
    runAction('complete', () =>
      markLeadTaskComplete(selectedTask.id, decision ? { result: decision, decision } : null),
    );

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <form
          className={styles.search}
          onSubmit={(event) => {
            event.preventDefault();
            setAppliedSearch(search);
          }}
        >
          <Search size={16} aria-hidden="true" />
          <input
            aria-label="Search lead tasks"
            placeholder="Search name, email, phone, or task"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </form>
        <select
          aria-label="Lead task owner"
          value={owner}
          onChange={(event) => setOwner(event.target.value)}
        >
          <option value="all">All owners</option>
          <option value="my">My tasks</option>
          <option value="unassigned">Unassigned</option>
        </select>
        <select
          aria-label="Lead task status"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value="open">Open</option>
          <option value="completed">Completed</option>
          <option value="all">All statuses</option>
        </select>
        <button
          className={styles.iconButton}
          type="button"
          title="Refresh lead tasks"
          onClick={loadTasks}
        >
          <RefreshCw size={16} aria-hidden="true" />
        </button>
      </div>

      {error ? <div className={styles.error}>{error}</div> : null}

      <div className={styles.workspace}>
        <div className={styles.taskList}>
          <div className={styles.listHeader}>
            <span>Leads</span>
            <span>{leadGroups.length}</span>
          </div>
          {isLoading ? <div className={styles.empty}>Loading lead tasks...</div> : null}
          {!isLoading && leadGroups.length === 0 ? (
            <div className={styles.empty}>No matching lead tasks</div>
          ) : null}
          {!isLoading &&
            leadGroups.map((group) => (
              <button
                key={group.leadResourceId}
                type="button"
                className={`${styles.taskRow} ${
                  group.leadResourceId === selectedLead?.leadResourceId ? styles.selected : ''
                }`}
                onClick={() => selectLead(group)}
              >
                <span className={styles.taskSubject}>{group.name}</span>
                <span className={styles.leadName}>
                  {group.activeTask?.subject || 'No open tasks'}
                </span>
                <span className={styles.taskMeta}>
                  <span>
                    {group.openCount} open / {group.tasks.length}
                  </span>
                  <span>{formatDueDate(group.activeTask?.dueAt)}</span>
                </span>
              </button>
            ))}
        </div>

        <div className={styles.detail}>
          {!selectedTask ? <div className={styles.empty}>Select a lead task</div> : null}
          {selectedTask ? (
            <>
              <header className={styles.detailHeader}>
                <div>
                  <div className={styles.eyebrow}>Lead workflow</div>
                  <h2>{formatLeadName(selectedTask.lead)}</h2>
                  <p>{selectedTask.subject}</p>
                </div>
                <span className={styles.status}>
                  {STATUS_LABELS[selectedTask.status] || 'Unknown'}
                </span>
              </header>

              <div className={styles.taskStrip}>
                {(selectedLead?.tasks || []).map((task) => (
                  <button
                    key={task.id}
                    type="button"
                    className={`${styles.stripRow} ${
                      task.id === selectedTaskId ? styles.stripSelected : ''
                    } ${isOpenStatus(task.status) ? '' : styles.stripDone}`}
                    onClick={() => setSelectedTaskId(task.id)}
                  >
                    <span>{task.subject}</span>
                    <span className={styles.stripMeta}>
                      {STATUS_LABELS[task.status] || 'Unknown'}
                      {isOpenStatus(task.status) && !task.workflowReady ? ' · waiting' : ''}
                    </span>
                  </button>
                ))}
              </div>

              <div className={styles.summary}>
                <div>
                  <span>Email</span>
                  <strong>{selectedTask.lead?.email || 'Not provided'}</strong>
                </div>
                <div>
                  <span>Phone</span>
                  <strong>{selectedTask.lead?.phone || 'Not provided'}</strong>
                </div>
                <div>
                  <span>Business unit</span>
                  <strong>{selectedTask.lead?.businessUnit || 'Not set'}</strong>
                </div>
                <div>
                  <span>Owner</span>
                  <strong>{selectedTask.assigneeName || 'Unassigned'}</strong>
                </div>
              </div>

              {isConsentTask ? (
                <div className={styles.consentPanel}>
                  <div>
                    <strong>Consent form</strong>
                    {consentDispatch ? (
                      <p>
                        Sent {formatDueDate(consentDispatch.sent_at)} ·{' '}
                        <a href={consentDispatch.consent_url} target="_blank" rel="noreferrer">
                          {consentDispatch.consent_url}
                        </a>
                      </p>
                    ) : (
                      <p>Not sent yet. Sending texts the lead their prefilled consent form.</p>
                    )}
                  </div>
                  <button
                    type="button"
                    className={styles.primaryButton}
                    disabled={!!activeAction || !isClaimedByCurrentUser}
                    onClick={sendConsent}
                    title={
                      isClaimedByCurrentUser
                        ? ''
                        : 'Claim this task before sending the consent form'
                    }
                  >
                    <Send size={16} aria-hidden="true" />
                    {/* eslint-disable-next-line no-nested-ternary */}
                    {activeAction === 'consent'
                      ? 'Sending...'
                      : consentDispatch
                        ? 'Resend consent form'
                        : 'Send consent form'}
                  </button>
                </div>
              ) : null}

              <div className={styles.actions}>
                {!selectedTask.assignee ? (
                  <button
                    type="button"
                    className={styles.primaryButton}
                    disabled={!!activeAction}
                    onClick={() => runAction('claim', () => assignLeadTaskToMe(selectedTask.id))}
                  >
                    <UserPlus size={16} aria-hidden="true" />
                    {activeAction === 'claim' ? 'Claiming...' : 'Claim task'}
                  </button>
                ) : null}
                {selectedTask.assignee && !isClaimedByCurrentUser ? (
                  <span className={styles.assignedNotice}>
                    Assigned to {selectedTask.assigneeName || 'another user'}
                  </span>
                ) : null}
                {isClaimedByCurrentUser && !selectedTask.workflowReady ? (
                  <span className={styles.assignedNotice}>
                    Waiting for the preceding workflow step
                  </span>
                ) : null}
                {isClaimedByCurrentUser &&
                selectedTask.workflowReady &&
                selectedTask.status !== 3 ? (
                  decisionOptions.length ? (
                    decisionOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={
                          option.value === 'disqualified'
                            ? styles.dangerButton
                            : styles.primaryButton
                        }
                        disabled={!!activeAction}
                        onClick={() => completeTask(option.value)}
                      >
                        <Check size={16} aria-hidden="true" />
                        {activeAction === 'complete' ? 'Submitting...' : option.label}
                      </button>
                    ))
                  ) : (
                    <button
                      type="button"
                      className={styles.primaryButton}
                      disabled={!!activeAction}
                      onClick={() => completeTask()}
                    >
                      <Check size={16} aria-hidden="true" />
                      {activeAction === 'complete' ? 'Submitting...' : 'Mark complete'}
                    </button>
                  )
                ) : null}
              </div>

              <LeadResourceCallForm
                leadResourceId={selectedTask.leadResourceId}
                onLeadUpdated={loadTasks}
                onCreatePatient={
                  isCreatePatientTask && isClaimedByCurrentUser ? createPatient : null
                }
                title="Lead details"
              />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

LeadTasksView.propTypes = {
  onCountChange: PropTypes.func,
  requestedTaskId: PropTypes.string,
  onSelectedTaskChange: PropTypes.func,
};

export default LeadTasksView;
